/**
 * K2Page - K2 Årsredovisning cluster page
 *
 * SEO Target: /arsredovisning/k2
 * Primary keywords: K2 årsredovisning, mindre företag årsredovisning, BFNAR 2016:10
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { PageMeta, getBreadcrumbSchema, SEO_CONFIG } from "@/lib/seo";
import { Breadcrumbs } from "@/layouts/PublicLayout";
import { Helmet } from "react-helmet-async";
import { AuthModal } from "@/components/auth/AuthModal";
import { usePricing } from "@/hooks/use-pricing";

const CheckIcon = () => (
  <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const XIcon = () => (
  <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
);

const ArrowRightIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

export function K2Page() {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const pricing = usePricing();

  const breadcrumbItems = [
    { label: "Årsredovisning", href: "/arsredovisning" },
    { label: "K2" },
  ];

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: "Hem", url: "/" },
    { name: "Årsredovisning", url: "/arsredovisning" },
    { name: "K2", url: "/arsredovisning/k2" },
  ]);

  return (
    <>
      <PageMeta
        title="K2 Årsredovisning Online | För Mindre Aktiebolag | Kvanten"
        description={`Skapa K2-årsredovisning enligt BFNAR 2016:10 för mindre företag. Förenklad redovisning, färre noter, schablonmässig värdering. Från ${pricing.formattedPriceExclVat}.`}
        keywords="K2 årsredovisning, BFNAR 2016:10, mindre företag årsredovisning, förenklad årsredovisning, K2 regelverk, årsredovisning aktiebolag"
        canonicalUrl={`${SEO_CONFIG.siteUrl}/arsredovisning/k2`}
        fullTitle
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-green-50 to-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={breadcrumbItems} />

          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              K2 Årsredovisning för Mindre Företag
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              K2 (BFNAR 2016:10) är det förenklade regelverket som passar de flesta svenska
              aktiebolag. Färre noter, enklare värdering och snabbare process.
            </p>

            <div className="flex flex-wrap gap-4 mb-8">
              <Link
                to="/wizard"
                className="inline-flex items-center gap-2 px-6 py-3 text-lg font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
              >
                Skapa K2-årsredovisning
                <ArrowRightIcon />
              </Link>
              <button
                onClick={() => setShowAuthModal(true)}
                className="inline-flex items-center gap-2 px-6 py-3 text-lg font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Skapa konto
              </button>
            </div>

            <div className="flex flex-wrap gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <CheckIcon />
                <span>Gratis att testa</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckIcon />
                <span>Betala endast vid export</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckIcon />
                <span>Klart på 30 min</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who can use K2 */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Vem får använda K2?</h2>
              <p className="text-gray-600 mb-6">
                K2 får tillämpas av <strong>mindre företag</strong> enligt BFNAR 2006:11.
                Ett företag är "mindre" om det inte överskrider mer än ett av följande
                gränsvärden under vart och ett av de två senaste räkenskapsåren:
              </p>
              <ul className="space-y-4">
                {[
                  "Medelantal anställda: högst 50",
                  "Balansomslutning: högst 40 miljoner kr",
                  "Nettoomsättning: högst 80 miljoner kr",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckIcon />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-gray-600">
                Det innebär att <strong>de allra flesta svenska aktiebolag</strong> kvalificerar
                för K2.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Vem får EJ använda K2?</h3>
              <div className="bg-red-50 rounded-xl p-6">
                <ul className="space-y-3">
                  {[
                    "Publika aktiebolag",
                    "Moderföretag i större koncern",
                    "Företag som upprättar koncernredovisning",
                    "Bostadsrättsföreningar (från 2026)",
                    "Företag med kryptotillgångar (från 2026)",
                    "Företag med utländska filialer (från 2026)",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <XIcon />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <p className="mt-4 text-sm text-gray-500">
                Se{" "}
                <Link to="/arsredovisning/guider/nya-regler-2026" className="text-blue-600 hover:underline">
                  nya regler från 2026
                </Link>{" "}
                för mer information om kommande förändringar.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* K2 Simplifications */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">K2-förenklingar</h2>
            <p className="text-xl text-gray-600">
              Dessa förenklingar gör K2 till ett praktiskt val för de flesta småföretag
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Färre noter",
                description:
                  "K2 kräver cirka 15 noter jämfört med K3:s 25-30. Du redovisar endast det mest väsentliga.",
              },
              {
                title: "Ingen uppskjuten skatt",
                description:
                  "K2-företag behöver inte beräkna eller redovisa uppskjuten skatt, vilket förenklar bokslutet betydligt.",
              },
              {
                title: "Schablonmässig avskrivning",
                description:
                  "Anläggningstillgångar kan skrivas av med schablon (5 år) om inte annat kan visas.",
              },
              {
                title: "Förenklad intäktsredovisning",
                description:
                  "Tydliga regler för när intäkter ska redovisas, med möjlighet till alternativregeln för tjänsteuppdrag.",
              },
              {
                title: "Inga jämförelsetal i noter",
                description:
                  "Jämförelsetal för föregående år i noter är frivilligt enligt K2.",
              },
              {
                title: "Enklare varuvärdering",
                description:
                  "Varulagret värderas enligt först-in-först-ut (FIFU) eller annat enkelt sätt.",
              },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's included in K2 */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Vad ingår i en K2-årsredovisning?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Obligatoriska delar</h3>
              <ul className="space-y-3">
                {[
                  "Förvaltningsberättelse med verksamhetsbeskrivning",
                  "Flerårsöversikt (4 år)",
                  "Förändring av eget kapital",
                  "Förslag till resultatdisposition",
                  "Resultaträkning",
                  "Balansräkning",
                  "Noter och tilläggsupplysningar",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckIcon />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Obligatoriska noter (K2)</h3>
              <ul className="space-y-3">
                {[
                  "Redovisningsprinciper",
                  "Anläggningstillgångar (om saldo finns)",
                  "Medelantal anställda (om personal finns)",
                  "Ställda säkerheter (om lån finns)",
                  "Eventualförpliktelser",
                  "Eget kapital-förändringar",
                  "Obeskattade reserver (om saldo finns)",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckIcon />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Legal references */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Juridisk grund</h2>
          <p className="text-gray-600 mb-8">
            K2-årsredovisning följer dessa regelverk:
          </p>
          <div className="bg-white rounded-xl p-8 shadow-sm">
            <ul className="space-y-4">
              <li>
                <strong className="text-gray-900">BFNAR 2016:10</strong>
                <span className="text-gray-600">
                  {" "}
                  - Bokföringsnämndens allmänna råd om årsredovisning i mindre företag (K2)
                </span>
              </li>
              <li>
                <strong className="text-gray-900">ÅRL (1995:1554)</strong>
                <span className="text-gray-600"> - Årsredovisningslagen</span>
              </li>
              <li>
                <strong className="text-gray-900">BFNAR 2006:11</strong>
                <span className="text-gray-600"> - Gränsvärden för större/mindre företag</span>
              </li>
              <li>
                <strong className="text-gray-900">ABL (2005:551)</strong>
                <span className="text-gray-600">
                  {" "}
                  - Aktiebolagslagen (för AB-specifika krav)
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-green-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Skapa din K2-årsredovisning idag
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Ladda upp SIE-fil och ha din årsredovisning klar på under 30 minuter.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/wizard"
              className="inline-flex items-center gap-2 px-8 py-4 text-lg font-medium text-green-600 bg-white rounded-lg hover:bg-green-50 transition-colors"
            >
              Prova direkt
              <ArrowRightIcon />
            </Link>
            <button
              onClick={() => setShowAuthModal(true)}
              className="inline-flex items-center gap-2 px-8 py-4 text-lg font-medium text-white border-2 border-white/30 rounded-lg hover:bg-white/10 transition-colors"
            >
              Skapa konto
            </button>
          </div>
        </div>
      </section>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="register"
      />
    </>
  );
}
